import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Flex, Grid, GridItem, Heading, Spinner, Text, VStack } from '@chakra-ui/react';
import { Header } from '../components/Header';
import {
  AutoBoletoResponse,
  BoletoListFilters,
  BoletoPageResponse,
  billingService
} from '../services/billingService';

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL'
});

const STATUS_OPTIONS = ['', 'PENDENTE', 'EMITIDO', 'PAGO', 'CANCELADO', 'VENCIDO', 'ERRO'];

/** Filtro já submetido — o que a listagem está de fato mostrando. */
type AppliedFilter = { status: string; busca: string };

const selectStyle: React.CSSProperties = {
  width: '100%',
  height: '40px',
  borderRadius: '0.375rem',
  border: '1px solid #E2E8F0',
  backgroundColor: '#FFFFFF',
  padding: '0 0.75rem',
  fontSize: '0.95rem'
};

const formatDate = (value: string | null): string => {
  if (!value) return '-';
  const [y, m, d] = value.split('-');
  if (y && m && d) return `${d}/${m}/${y}`;
  return value;
};

const formatDateTime = (value: string): string => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('pt-BR');
};

/**
 * Identificador humano do boleto: o número da fatura. É gerado antes do primeiro save, portanto
 * existe já no PENDENTE — diferente do numeroBoleto, que só chega depois da emissão na OMIE. O
 * codigoIntegracao NÃO serve aqui: é a chave de idempotência da saga ("anubis-boleto-<uuid>").
 * Registros anteriores à migration V38 não têm numeroFatura e caem no id curto.
 */
const boletoLabel = (b: AutoBoletoResponse): string => b.numeroFatura ?? `#${b.id.slice(0, 8)}`;

const getStatusBadgeStyle = (status: string): { bg: string; color: string; border: string } => {
  switch (status) {
    case 'EMITIDO':
      return { bg: '#DBEAFE', color: '#1E40AF', border: '1px solid #BFDBFE' };
    case 'PAGO':
      return { bg: '#D1FAE5', color: '#065F46', border: '1px solid #A7F3D0' };
    case 'PENDENTE':
      return { bg: '#FFF3CD', color: '#856404', border: '1px solid #FFEAA0' };
    case 'CANCELADO':
      return { bg: '#F3F4F6', color: '#374151', border: '1px solid #E5E7EB' };
    case 'VENCIDO':
      return { bg: '#FFE4E6', color: '#9F1239', border: '1px solid #FECDD3' };
    case 'ERRO':
      return { bg: '#FEE2E2', color: '#991B1B', border: '1px solid #FCA5A5' };
    default:
      return { bg: '#F3F4F6', color: '#374151', border: '1px solid #E5E7EB' };
  }
};

export const BoletosAdminPage: React.FC = () => {
  const [status, setStatus] = useState('');
  const [busca, setBusca] = useState('');
  /**
   * Filtro aplicado, separado dos inputs: digitar no campo de busca não refaz a consulta a cada
   * tecla, e paginação, troca de tamanho de página e recargas pós-ação preservam o que está filtrado.
   */
  const [applied, setApplied] = useState<AppliedFilter>({ status: '', busca: '' });
  const [pageSize, setPageSize] = useState(5);
  const [items, setItems] = useState<AutoBoletoResponse[]>([]);
  const [pageState, setPageState] = useState<BoletoPageResponse>({
    content: [],
    totalElements: 0,
    totalPages: 0,
    size: 10,
    number: 0,
    first: true,
    last: true
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [cancelingId, setCancelingId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [reemitindoId, setReemitindoId] = useState<string | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);

  const load = useCallback(async (filter: AppliedFilter, page: number, size: number) => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const filters: BoletoListFilters = {
        status: filter.status,
        busca: filter.busca,
        page,
        size,
        sort: 'createdAt,desc'
      };
      const data = await billingService.listBoletos(filters);
      setItems(data.content);
      setPageState(data);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Não foi possível carregar a lista de boletos. Por favor, tente novamente.');
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load(applied, 0, pageSize);
  }, [load, applied, pageSize]);

  const handleApplyFilter = () => setApplied({ status, busca });

  const handlePage = (page: number) => {
    if (page < 0 || page >= pageState.totalPages) return;
    load(applied, page, pageSize);
  };

  const handlePageSizeChange = (value: string) => {
    const parsed = Number(value);
    if (![5, 10, 25, 50, 100].includes(parsed)) return;
    setPageSize(parsed);
  };

  const handleDownloadPdf = async (boleto: AutoBoletoResponse) => {
    try {
      setDownloadingId(boleto.id);
      setErrorMessage(null);
      await billingService.downloadBoletoPdf(boleto.id);
    } catch (error) {
      setErrorMessage(`Boleto ${boletoLabel(boleto)}: ${error instanceof Error ? error.message : 'Não foi possível baixar o PDF do boleto. Por favor, tente novamente.'}`);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleCancel = async (boleto: AutoBoletoResponse) => {
    const ok = window.confirm(
      `Cancelar o boleto ${boletoLabel(boleto)} — ${currencyFormatter.format(boleto.valorTotal)}, ${boleto.qtdeVeiculos ?? '?'} veículo(s)? Os veículos vinculados voltam para "Estoque".`
    );
    if (!ok) return;
    try {
      setCancelingId(boleto.id);
      setErrorMessage(null);
      setSuccessMessage(null);
      await billingService.cancelBoleto(boleto.id);
      setSuccessMessage(`Boleto ${boletoLabel(boleto)} cancelado.`);
      await load(applied, pageState.number, pageSize);
    } catch (error) {
      setErrorMessage(`Boleto ${boletoLabel(boleto)}: ${error instanceof Error ? error.message : 'Não foi possível cancelar o boleto. Por favor, tente novamente.'}`);
    } finally {
      setCancelingId(null);
    }
  };

  const handleReemitir = async (boleto: AutoBoletoResponse) => {
    try {
      setReemitindoId(boleto.id);
      setErrorMessage(null);
      setSuccessMessage(null);
      await billingService.reemitirBoleto(boleto.id);
      setSuccessMessage(`Boleto ${boletoLabel(boleto)} reenviado para emissão.`);
      await load(applied, pageState.number, pageSize);
    } catch (error) {
      setErrorMessage(`Boleto ${boletoLabel(boleto)}: ${error instanceof Error ? error.message : 'Não foi possível reemitir o boleto. Por favor, tente novamente.'}`);
    } finally {
      setReemitindoId(null);
    }
  };

  const handleSync = async (boleto: AutoBoletoResponse) => {
    try {
      setSyncingId(boleto.id);
      setErrorMessage(null);
      setSuccessMessage(null);
      await billingService.syncBoleto(boleto.id);
      setSuccessMessage(`Boleto ${boletoLabel(boleto)} sincronizado.`);
      await load(applied, pageState.number, pageSize);
    } catch (error) {
      setErrorMessage(`Boleto ${boletoLabel(boleto)}: ${error instanceof Error ? error.message : 'Não foi possível sincronizar o boleto. Por favor, tente novamente.'}`);
    } finally {
      setSyncingId(null);
    }
  };

  const canCancel = (s: string) => s === 'EMITIDO' || s === 'VENCIDO' || s === 'PENDENTE' || s === 'ERRO';
  const canDownloadPdf = (s: string) => s !== 'CANCELADO' && s !== 'ERRO' && s !== 'PENDENTE';
  const canReemitir = (s: string) => s === 'ERRO';
  const canSync = (s: string) => s === 'EMITIDO' || s === 'PENDENTE';

  return (
    <Box bg="#F5F5F5" minH="100vh" display="flex" flexDirection="column">
      <Header title="Sistema de Efetivação de Apólices" subtitle="Boletos" />

      <Box py={8} px={4} display="flex" justifyContent="center">
        <Box
          maxW={{ base: '98%', md: '95%', lg: '92%', xl: '90%' }}
          w="100%"
          bg="white"
          borderRadius="xl"
          overflow="hidden"
          boxShadow="0 4px 20px rgba(0,0,0,0.08)"
        >
          <Box p={{ base: 4, md: 6 }}>
            <VStack gap={5} alignItems="stretch">
              {/* Cabeçalho */}
              <Flex
                justifyContent="space-between"
                alignItems={{ base: 'flex-start', md: 'center' }}
                flexDirection={{ base: 'column', md: 'row' }}
                gap={2}
              >
                <Heading size="lg" color="#1A1A1A">
                  Listagem de Boletos
                </Heading>
                <Text fontSize="sm" color="gray.600" fontWeight="500">
                  {pageState.totalElements} boleto(s) encontrado(s)
                </Text>
              </Flex>

              {/* Filtros */}
              <Box p={4} bg="gray.50" borderRadius="lg" borderWidth="1px" borderColor="gray.200">
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={3}>
                  <GridItem>
                    <Text fontSize="xs" color="gray.600" mb={1} fontWeight="600" textTransform="uppercase">
                      Status
                    </Text>
                    <select value={status} onChange={(e) => setStatus(e.target.value)} style={selectStyle}>
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt || 'all'} value={opt}>
                          {opt || 'Todos'}
                        </option>
                      ))}
                    </select>
                  </GridItem>
                  <GridItem>
                    <Text fontSize="xs" color="gray.600" mb={1} fontWeight="600" textTransform="uppercase">
                      Nº da fatura ou do boleto
                    </Text>
                    <input
                      type="text"
                      value={busca}
                      onChange={(e) => setBusca(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleApplyFilter();
                      }}
                      placeholder="FAT-2026-000147, 147 ou nº do boleto"
                      style={selectStyle}
                    />
                  </GridItem>
                </Grid>

                <Flex mt={4} gap={2} justifyContent="flex-end" flexWrap="wrap">
                  <Button
                    bg="linear-gradient(135deg, #3333CC 0%, #2828a3 100%)"
                    color="white"
                    _hover={{ bg: '#2828a3' }}
                    onClick={handleApplyFilter}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Consultando...' : 'Filtrar'}
                  </Button>
                </Flex>
              </Box>

              {/* Mensagem de erro */}
              {errorMessage && (
                <Box p={4} bg="red.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="red.500">
                  <Text color="red.700" fontWeight="600">
                    {errorMessage}
                  </Text>
                </Box>
              )}

              {/* Mensagem de sucesso */}
              {successMessage && (
                <Box p={4} bg="green.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="green.500">
                  <Text color="green.700" fontWeight="600">
                    {successMessage}
                  </Text>
                </Box>
              )}

              {/* Tabela */}
              <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" overflow="hidden">
                {isLoading ? (
                  <Flex py={12} justifyContent="center" alignItems="center" gap={2}>
                    <Spinner size="sm" color="#3333CC" />
                    <Text fontSize="sm" color="gray.600">
                      Carregando boletos...
                    </Text>
                  </Flex>
                ) : items.length === 0 ? (
                  <Flex py={12} justifyContent="center" alignItems="center">
                    <Text fontSize="sm" color="gray.600">
                      Nenhum boleto encontrado.
                    </Text>
                  </Flex>
                ) : (
                  <Box overflowX="auto">
                    <Box as="table" width="100%" fontSize="sm">
                      <Box as="thead" bg="gray.100">
                        <Box as="tr">
                          <Box as="th" p={3} textAlign="left">Fatura</Box>
                          <Box as="th" p={3} textAlign="left">Status</Box>
                          <Box as="th" p={3} textAlign="center">Tentativas</Box>
                          <Box as="th" p={3} textAlign="right">Valor</Box>
                          <Box as="th" p={3} textAlign="center">Veículos</Box>
                          <Box as="th" p={3} textAlign="left">Vencimento</Box>
                          <Box as="th" p={3} textAlign="left">Pagamento</Box>
                          <Box as="th" p={3} textAlign="left">Nº Boleto</Box>
                          <Box as="th" p={3} textAlign="left">Criado</Box>
                          <Box as="th" p={3} textAlign="center" minW="240px">Ações</Box>
                        </Box>
                      </Box>
                      <Box as="tbody">
                        {items.map((b) => {
                          const badge = getStatusBadgeStyle(b.status);
                          return (
                            <Box
                              as="tr"
                              key={b.id}
                              borderTopWidth="1px"
                              borderTopColor="gray.100"
                              _hover={{ bg: 'gray.50' }}
                            >
                              <Box
                                as="td"
                                p={3}
                                fontFamily="mono"
                                fontSize="sm"
                                fontWeight="600"
                                color={b.numeroFatura ? undefined : 'gray.500'}
                                title={b.codigoIntegracao}
                              >
                                {boletoLabel(b)}
                              </Box>
                              <Box as="td" p={3}>
                                <Box
                                  as="span"
                                  display="inline-block"
                                  px={2}
                                  py="2px"
                                  borderRadius="full"
                                  fontSize="xs"
                                  fontWeight="700"
                                  bg={badge.bg}
                                  color={badge.color}
                                  border={badge.border}
                                  whiteSpace="nowrap"
                                  cursor={b.status === 'ERRO' && b.lastError ? 'help' : undefined}
                                  title={b.status === 'ERRO' && b.lastError ? b.lastError : undefined}
                                >
                                  {b.status}
                                </Box>
                                {b.status === 'ERRO' && b.lastError && (
                                  <Text mt={1} fontSize="xs" color="red.600" maxW="220px" title={b.lastError}>
                                    {b.lastError.length > 50 ? `${b.lastError.slice(0, 50)}...` : b.lastError}
                                  </Text>
                                )}
                              </Box>
                              <Box as="td" p={3} textAlign="center">{b.attempts ?? '-'}</Box>
                              <Box as="td" p={3} textAlign="right" fontWeight="600">
                                {currencyFormatter.format(b.valorTotal)}
                              </Box>
                              <Box as="td" p={3} textAlign="center">{b.qtdeVeiculos ?? '-'}</Box>
                              <Box as="td" p={3}>{formatDate(b.dataVencimento)}</Box>
                              <Box as="td" p={3}>{formatDate(b.dataPagamento)}</Box>
                              <Box as="td" p={3} fontFamily="mono" fontSize="xs">
                                {b.numeroBoleto || '-'}
                              </Box>
                              <Box as="td" p={3} fontSize="xs" color="gray.600">
                                {formatDateTime(b.createdAt)}
                              </Box>
                              <Box as="td" p={3}>
                                <Flex gap={1} flexWrap="wrap" justifyContent="center">
                                  {canDownloadPdf(b.status) && (
                                    <Button
                                      size="xs"
                                      colorScheme="blue"
                                      variant="outline"
                                      onClick={() => handleDownloadPdf(b)}
                                      disabled={downloadingId === b.id}
                                      minW="60px"
                                    >
                                      {downloadingId === b.id ? <Spinner size="xs" /> : 'PDF'}
                                    </Button>
                                  )}
                                  {canReemitir(b.status) && (
                                    <Button
                                      size="xs"
                                      colorScheme="orange"
                                      variant="outline"
                                      onClick={() => handleReemitir(b)}
                                      disabled={reemitindoId === b.id}
                                      minW="75px"
                                    >
                                      {reemitindoId === b.id ? <Spinner size="xs" /> : 'Reemitir'}
                                    </Button>
                                  )}
                                  {canSync(b.status) && (
                                    <Button
                                      size="xs"
                                      colorScheme="blue"
                                      variant="outline"
                                      onClick={() => handleSync(b)}
                                      disabled={syncingId === b.id}
                                      minW="90px"
                                    >
                                      {syncingId === b.id ? <Spinner size="xs" /> : 'Sincronizar'}
                                    </Button>
                                  )}
                                  {canCancel(b.status) && (
                                    <Button
                                      size="xs"
                                      colorScheme="red"
                                      variant="outline"
                                      onClick={() => handleCancel(b)}
                                      disabled={cancelingId === b.id}
                                      minW="75px"
                                    >
                                      {cancelingId === b.id ? <Spinner size="xs" /> : 'Cancelar'}
                                    </Button>
                                  )}
                                </Flex>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Paginação */}
              <Flex justifyContent="space-between" alignItems="center" gap={3} flexWrap="wrap">
                <Text fontSize="sm" color="gray.600">
                  Página {pageState.totalPages > 0 ? pageState.number + 1 : 0} de {pageState.totalPages}
                </Text>
                <Flex gap={2} alignItems="center" flexWrap="wrap">
                  <Flex alignItems="center" gap={2}>
                    <Text fontSize="sm" color="gray.600">
                      Itens por página
                    </Text>
                    <select
                      value={String(pageSize)}
                      onChange={(e) => handlePageSizeChange(e.target.value)}
                      disabled={isLoading}
                      style={{
                        height: '36px',
                        borderRadius: '0.375rem',
                        border: '1px solid #E2E8F0',
                        backgroundColor: '#FFFFFF',
                        padding: '0 0.5rem',
                        fontSize: '0.9rem'
                      }}
                    >
                      <option value="5">5 (padrão)</option>
                      <option value="10">10</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </Flex>
                  <Button
                    variant="outline"
                    colorScheme="gray"
                    size="sm"
                    onClick={() => handlePage(pageState.number - 1)}
                    disabled={isLoading || pageState.first}
                  >
                    Anterior
                  </Button>
                  <Button
                    bg="linear-gradient(135deg, #3333CC 0%, #2828a3 100%)"
                    color="white"
                    _hover={{ bg: '#2828a3' }}
                    size="sm"
                    onClick={() => handlePage(pageState.number + 1)}
                    disabled={isLoading || pageState.last || pageState.totalPages === 0}
                  >
                    Próxima
                  </Button>
                </Flex>
              </Flex>
            </VStack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
